import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { X } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { api, formatApiErrorDetail } from "@/lib/apiClient";

const pin = L.divIcon({
  html: `<div style="width:22px;height:22px;border-radius:50%;background:#E27D60;border:3px solid white;box-shadow:0 3px 8px rgba(0,0,0,0.35);"></div>`,
  className: "surf-edit-pin",
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

function ClickToPlace({ onPick }) {
  useMapEvents({
    click(e) { onPick(e.latlng.lat, e.latlng.lng); },
  });
  return null;
}

const EMPTY = {
  name: "",
  slug: "",
  country: "",
  region: "",
  nearest_town: "",
  lat: 50,
  lon: -5,
  timezone: "UTC",
  visibility: "public_beach",
  break_type: "beach",
  min_ability: "beginner",
  max_ability: "intermediate",
  beginner_suitable: true,
  intermediate_suitable: true,
  typical_water_temp_c: null,
  conditions: {
    preferred_swell_dir: [],
    acceptable_swell_dir: [],
    preferred_wind_dir: [],
    poor_wind_dir: [],
    preferred_tide: "any",
    min_wave_m: 0.4,
    max_wave_m: 2.5,
    seasonal_reliability: "moderate",
  },
  safety: {
    rip_current_risk: "moderate",
    rock_reef_risk: "low",
    shore_break_risk: "low",
    tidal_cutoff_risk: "low",
    lifeguard_available: false,
    lifeguard_details: null,
    general_hazards: [],
  },
  facilities: {
    parking: true, public_transport: false, toilets: false, showers: false,
    changing_rooms: false, accessible: false,
  },
  short_description: "",
  beginner_summary: "",
  intermediate_summary: "",
  safety_summary: "",
  local_etiquette: null,
  image_url: null,
  published: true,
};

export default function SurfAreaEditModal({ area, onClose, onSaved }) {
  const [f, setF] = useState(() => ({ ...EMPTY, ...(area || {}) }));
  const [busy, setBusy] = useState(false);
  useEffect(() => { setF({ ...EMPTY, ...(area || {}) }); }, [area]);
  const isEdit = !!area?.id;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...f,
        conditions: f.conditions,
        safety: f.safety,
        facilities: f.facilities,
      };
      delete payload.id;
      delete payload.created_at;
      delete payload.updated_at;
      if (isEdit) {
        await api.put(`/admin/surf-areas/${area.id}`, payload);
        toast.success("Surf area updated");
      } else {
        await api.post("/admin/surf-areas", payload);
        toast.success("Surf area created");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Please review the fields");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start md:items-center justify-center p-4" data-testid="admin-area-edit-modal">
      <div className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <div className="overline">Admin surf-area</div>
            <div className="font-display text-lg text-teal-900">{isEdit ? "Edit surf area" : "New surf area"}</div>
          </div>
          <button onClick={onClose} data-testid="admin-area-close" className="w-9 h-9 rounded-full hover:bg-sand-200 flex items-center justify-center"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-6 overflow-y-auto space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <label className="col-span-2"><span className="overline">Name</span>
              <Input data-testid="area-form-name" required className="h-10 mt-1" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
            </label>
            <label><span className="overline">Slug</span>
              <Input data-testid="area-form-slug" required className="h-10 mt-1" value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} />
            </label>
            <label><span className="overline">Country</span>
              <Input data-testid="area-form-country" required className="h-10 mt-1" value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} />
            </label>
            <label><span className="overline">Region</span>
              <Input data-testid="area-form-region" required className="h-10 mt-1" value={f.region} onChange={(e) => setF({ ...f, region: e.target.value })} />
            </label>
            <label><span className="overline">Nearest town</span>
              <Input className="h-10 mt-1" value={f.nearest_town || ""} onChange={(e) => setF({ ...f, nearest_town: e.target.value })} />
            </label>
          </div>

          <div>
            <div className="overline">Location — click the map to place the pin</div>
            <div className="mt-2 rounded-xl overflow-hidden border border-border" style={{ height: 300 }}>
              <MapContainer center={[f.lat || 0, f.lon || 0]} zoom={7} style={{ height: "100%", width: "100%" }}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OSM' />
                <ClickToPlace onPick={(lat, lon) => setF((s) => ({ ...s, lat: Number(lat.toFixed(5)), lon: Number(lon.toFixed(5)) }))} />
                {f.lat != null && f.lon != null && <Marker position={[f.lat, f.lon]} icon={pin} />}
              </MapContainer>
            </div>
            <div className="mt-2 grid grid-cols-3 gap-3">
              <label><span className="overline">Latitude</span>
                <Input data-testid="area-form-lat" type="number" step="any" className="h-10 mt-1" value={f.lat} onChange={(e) => setF({ ...f, lat: Number(e.target.value) })} />
              </label>
              <label><span className="overline">Longitude</span>
                <Input data-testid="area-form-lon" type="number" step="any" className="h-10 mt-1" value={f.lon} onChange={(e) => setF({ ...f, lon: Number(e.target.value) })} />
              </label>
              <label><span className="overline">Time zone</span>
                <Input className="h-10 mt-1" value={f.timezone} onChange={(e) => setF({ ...f, timezone: e.target.value })} />
              </label>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <label><span className="overline">Visibility</span>
              <select className="mt-1 h-10 w-full rounded-md border border-border px-3 text-sm" data-testid="area-form-visibility" value={f.visibility} onChange={(e) => setF({ ...f, visibility: e.target.value })}>
                <option value="public_beach">public_beach</option>
                <option value="broad_area">broad_area</option>
                <option value="sensitive">sensitive</option>
              </select>
            </label>
            <label><span className="overline">Break type</span>
              <select className="mt-1 h-10 w-full rounded-md border border-border px-3 text-sm" value={f.break_type} onChange={(e) => setF({ ...f, break_type: e.target.value })}>
                {["beach","point","reef","mixed","estuary","other"].map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </label>
            <label className="flex items-center gap-2 md:mt-6">
              <input type="checkbox" data-testid="area-form-published" checked={!!f.published} onChange={(e) => setF({ ...f, published: e.target.checked })} />
              <span className="text-sm">Published</span>
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label><span className="overline">Min ability</span>
              <select className="mt-1 h-10 w-full rounded-md border border-border px-3 text-sm" value={f.min_ability} onChange={(e) => setF({ ...f, min_ability: e.target.value })}>
                {["complete_beginner","beginner","improving_beginner","lower_intermediate","intermediate"].map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </label>
            <label><span className="overline">Max ability</span>
              <select className="mt-1 h-10 w-full rounded-md border border-border px-3 text-sm" value={f.max_ability} onChange={(e) => setF({ ...f, max_ability: e.target.value })}>
                {["complete_beginner","beginner","improving_beginner","lower_intermediate","intermediate"].map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </label>
          </div>

          <div>
            <span className="overline">Short description</span>
            <textarea className="mt-1 w-full h-20 rounded-md border border-border p-2 text-sm" value={f.short_description} onChange={(e) => setF({ ...f, short_description: e.target.value })} />
          </div>
          <div>
            <span className="overline">Safety summary</span>
            <textarea className="mt-1 w-full h-20 rounded-md border border-border p-2 text-sm" value={f.safety_summary} onChange={(e) => setF({ ...f, safety_summary: e.target.value })} />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="h-10 px-4 rounded-full text-sm text-muted-foreground hover:bg-sand-200">Cancel</button>
            <button type="submit" data-testid="area-form-submit" disabled={busy} className="h-10 px-5 rounded-full bg-teal-800 text-white text-sm font-medium hover:bg-teal-900 disabled:opacity-60">
              {busy ? "Saving…" : isEdit ? "Save changes" : "Create surf area"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
